import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { LedgerOptionsDto } from "@delta/shared";
import { groupPath } from "../accounting/common";
import { useLang, type T } from "../../i18n";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { Icon } from "../../components/Icon";
import { printReport, reportDoc, type ReportPrintData, type ReportPrintRow } from "../../print/report";
import { printUnrecorded, roleMapping, type AgentMapping } from "../../print/direct";
import { loadPrintImage } from "../../print/render";

// Pieces shared by the journal screens and the ledger reports.

/**
 * An exact decimal from the API ("-1234.500") for display: thousands separators, two decimals unless the third is
 * used (imported history keeps three). Never parsed as a floating-point number.
 */
export function amt(x: string | null | undefined): string {
  if (!x) return "0.00";
  const neg = x.startsWith("-");
  const [i, f = ""] = (neg ? x.slice(1) : x).split(".");
  const frac = f.length > 2 && f.slice(2).replace(/0+$/, "") ? f.replace(/0+$/, "") : f.slice(0, 2).padEnd(2, "0");
  const int = i!.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const zero = !/[1-9]/.test(int + frac);
  return `${neg && !zero ? "-" : ""}${int}.${frac}`;
}
export const isZero = (x: string) => !/[1-9]/.test(x);
const abs = (x: string) => (x.startsWith("-") ? x.slice(1) : x);

/** A balance as "1,200.00 Dr" or "300.00 Cr" (debit minus credit; zero has no side). */
export function DrCr({ v }: { v: string }) {
  const { t } = useLang();
  if (isZero(v)) return <span className="num">{amt("0")}</span>;
  return <span className="num">{amt(abs(v))} <small className="muted">{t(v.startsWith("-") ? "crS" : "drS")}</small></span>;
}
export const drcrText = (t: T, v: string) => (isZero(v) ? amt("0") : `${amt(abs(v))} ${t(v.startsWith("-") ? "crS" : "drS")}`);
export const Amt = ({ v, zero }: { v: string; zero?: boolean }) => (zero && isZero(v) ? <span className="muted num">–</span> : <span className="num">{amt(v)}</span>);

/** "2026-04-01T10:00" as "2026-04-01 10:00" (Western digits, as the prototype shows dates). */
export const when = (x: string) => x.replace("T", " ");
/**
 * A stored instant (UTC, ISO) in Saudi time as "YYYY-MM-DD HH:mm". Delta keeps business times in Asia/Riyadh (UTC+3,
 * no daylight saving), as the server dates them, so a posting time never shows in UTC or in the browser's own zone.
 */
export const instant = (iso: string) => new Date(Date.parse(iso) + 3 * 3600_000).toISOString().slice(0, 16).replace("T", " ");
/** Now in Saudi time, "YYYY-MM-DDTHH:mm": the default date of a new document, whatever zone the browser is in. */
export const nowRiyadh = () => new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 16);

/** Accounts, customers and suppliers, cost centers and treasuries for the line editor and the report filters. */
export function useLedgerOptions(): { opts: LedgerOptionsDto | null; failed: boolean } {
  const [opts, setOpts] = useState<LedgerOptionsDto | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api<LedgerOptionsDto>("GET", "/ledger/options").then(setOpts, () => setFailed(true)); }, []);
  return { opts, failed };
}

/** Accounts grouped under their group, each group labelled with its path from the class, in tree order. */
export function LedgerAccountOptions({ opts }: { opts: LedgerOptionsDto | null }) {
  const { L } = useLang();
  if (!opts) return null;
  return <>{opts.types.map((g) => {
    const accs = opts.accounts.filter((a) => a.typeId === g.id);
    return accs.length ? <optgroup key={g.id} label={groupPath(L, g)}>{accs.map((a) => <option key={a.id} value={a.id}>{a.code} {L(a)}</option>)}</optgroup> : null;
  })}</>;
}

// ---------- report frame (prototype erp-reports.js report shell) ----------

/** Period presets of the prototype that need no week rule: year, quarter, month, all dates or a custom range. */
const PRESETS = ["thisYear", "lastYear", "thisMonth", "lastMonth", "q1", "q2", "q3", "q4", "all", "custom"] as const;
const iso = (d: Date) => d.toISOString().slice(0, 10);
function range(k: string, today = new Date()): [string, string] {
  const y = today.getFullYear(), m = today.getMonth();
  const U = (yy: number, mm: number, dd: number) => iso(new Date(Date.UTC(yy, mm, dd)));
  switch (k) {
    case "thisYear": return [`${y}-01-01`, `${y}-12-31`];
    case "lastYear": return [`${y - 1}-01-01`, `${y - 1}-12-31`];
    case "thisMonth": return [U(y, m, 1), U(y, m + 1, 0)];
    case "lastMonth": return [U(y, m - 1, 1), U(y, m, 0)];
    case "q1": case "q2": case "q3": case "q4": { const q = Number(k[1]) - 1; return [U(y, q * 3, 1), U(y, q * 3 + 3, 0)]; }
    default: return ["", ""];
  }
}
export interface Period { preset: string; from: string; to: string }
export const initialPeriod = (preset = "thisYear"): Period => { const [from, to] = range(preset); return { preset, from, to }; };
export const periodQuery = (p: Period) => [p.from && `from=${p.from}`, p.to && `to=${p.to}`].filter(Boolean).join("&");

export interface Filter { k: string; label: string; options: [string, string][]; required?: boolean; wide?: boolean; text?: string }

/** Filter bar: period (optional), select filters, and Search. The results update only on Search, as in the prototype. */
export function ReportFilters({ period, filters, values, onSearch, testId }: {
  period?: Period | null; filters: Filter[]; values: Record<string, string>; testId?: string;
  onSearch(p: Period | null, v: Record<string, string>): void;
}) {
  const { t } = useLang();
  const [p, setP] = useState<Period | null>(period ?? null);
  const [v, setV] = useState(values);
  const dirty = JSON.stringify([p, v]) !== JSON.stringify([period ?? null, values]);
  const presetLabel = (k: string) => (/^q\d$/.test(k) ? t("prQ", { n: k[1]! }) : t("pr_" + k));
  // The filters as applied (not as edited), for the printed report's header.
  const meta = useContext(ReportMetaCtx);
  const applied = JSON.stringify([period ?? null, values, filters.map((f) => [f.k, f.label, f.options.length])]);
  useEffect(() => {
    if (!meta) return;
    const out: [string, string][] = [];
    if (period) out.push([t("rpPeriod"), period.from || period.to ? `${period.from || "…"} – ${period.to || "…"}` : t("pr_all")]);
    for (const f of filters) {
      const x = values[f.k];
      if (!x) continue;
      out.push([f.label, f.text !== undefined ? x : f.options.find(([a]) => a === x)?.[1] ?? x]);
    }
    meta.setApplied(out);
  }, [applied]);
  return (
    <section className="card rp-filter" data-testid={testId}>
      <div className="rp-fgrid">
        {p && <>
          <label className="field rp-period"><span className="label">{t("date")}</span>
            <select className="control" value={p.preset} onChange={(e) => { const k = e.target.value; const [from, to] = range(k); setP(k === "custom" ? { ...p, preset: k } : { preset: k, from, to }); }}>
              {PRESETS.map((k) => <option key={k} value={k}>{presetLabel(k)}</option>)}
            </select></label>
          <label className="field rp-date"><span className="label">{t("fromDate")}</span><input type="date" className="control num" value={p.from} onChange={(e) => setP({ ...p, preset: "custom", from: e.target.value })} /></label>
          <label className="field rp-date"><span className="label">{t("toDate")}</span><input type="date" className="control num" value={p.to} onChange={(e) => setP({ ...p, preset: "custom", to: e.target.value })} /></label>
        </>}
        {filters.map((f) => f.text !== undefined ? (
          <label key={f.k} className="field"><span className="label">{f.label}</span>
            <input type="search" className="control" value={v[f.k] ?? ""} placeholder={f.text} aria-label={f.label} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} /></label>
        ) : (
          <label key={f.k} className={`field ${f.wide ? "rp-wide" : ""}`}><span className={`label ${f.required ? "req" : ""}`}>{f.label}</span>
            <select className="control" value={v[f.k] ?? ""} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} aria-label={f.label}>
              <option value="">{f.required ? t("chooseDots") : t("all")}</option>
              {f.options.map(([a, b]) => <option key={a} value={a}>{b}</option>)}
            </select></label>
        ))}
        <div className="rp-go"><button className="btn btn-primary" data-testid="rp-search" onClick={() => onSearch(p, v)}><Icon name="search" />{t("search")}</button></div>
      </div>
      {dirty && <div className="rp-dirty"><Icon name="info" />{t("rpFiltersChanged")}</div>}
    </section>
  );
}

/** `screenOnly`: a column of links or buttons, left out of the printed report and the Excel file. */
export interface RCol<R> { label: string; grp?: string; end?: boolean; screenOnly?: boolean; cell(r: R): ReactNode; text(r: R): string }

/** Two-level headers: consecutive columns sharing `grp` get one spanning header (prototype headHtml). */
function Head<R>({ cols }: { cols: RCol<R>[] }) {
  if (!cols.some((c) => c.grp)) return <tr>{cols.map((c, i) => <th key={i} className={c.end ? "end" : ""}>{c.label}</th>)}</tr>;
  const top: ReactNode[] = [], bot: ReactNode[] = [];
  for (let i = 0; i < cols.length; i++) {
    const c = cols[i]!;
    if (!c.grp) { top.push(<th key={i} rowSpan={2} className={c.end ? "end" : ""}>{c.label}</th>); continue; }
    let n = 1; while (cols[i + n]?.grp === c.grp) n++;
    top.push(<th key={i} colSpan={n} className="rp-grp">{c.grp}</th>);
    for (let j = 0; j < n; j++) bot.push(<th key={i + j} className={cols[i + j]!.end ? "end" : ""}>{cols[i + j]!.label}</th>);
    i += n - 1;
  }
  return <><tr>{top}</tr><tr>{bot}</tr></>;
}

/** What a report page tells its tables for printing: the report's title and the filters as applied. */
export interface ReportMeta { title: string; applied: [string, string][]; setApplied(a: [string, string][]): void }
export const ReportMetaCtx = createContext<ReportMeta | null>(null);

/** The row kind on paper, from the screen's row class: opening, section heading, subtotal or grand total. */
const kindOf = (cls: string | undefined): ReportPrintRow["kind"] =>
  !cls ? undefined : /\brp-open\b/.test(cls) ? "open" : /\brp-h\b/.test(cls) ? "section" : /\brp-s\b/.test(cls) ? "subtotal" : /\brp-g\b/.test(cls) ? "total" : undefined;

let a4Cache: { at: number; m: AgentMapping | null } | null = null;
/** This PC's A4 printer through the Delta print agent, if one is mapped (checked once a minute at most). */
export function useA4Printer(): AgentMapping | null {
  const [m, setM] = useState<AgentMapping | null>(a4Cache?.m ?? null);
  useEffect(() => {
    if (a4Cache && Date.now() - a4Cache.at < 60_000) { setM(a4Cache.m); return; }
    let live = true;
    roleMapping("a4").then((r) => { a4Cache = { at: Date.now(), m: r.ok ? r.mapping : null }; if (live) setM(a4Cache.m); }, () => undefined);
    return () => { live = false; };
  }, []);
  return m;
}

/** The report's data as printed pages: every row the filters returned, never just what is on the screen. */
export function usePrintData() {
  const { t, lang } = useLang();
  const { me } = useSession();
  const meta = useContext(ReportMetaCtx);
  return (title: string, cols: { label: string; grp?: string; end?: boolean }[], rows: ReportPrintRow[], foot?: string[], notes?: string[]): ReportPrintData => {
    const at = new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 16).replace("T", " ");
    return {
      org: me ? (lang === "ar" ? me.organization.nameAr : me.organization.nameEn) : "",
      title: meta && meta.title !== title && !title.startsWith(meta.title) ? `${meta.title} – ${title}` : title,
      meta: meta?.applied ?? [], cols, rows, foot, notes, lang, printedAt: at, printedBy: me?.user.name ?? "",
      text: { page: (i, n) => t("rpPrintPage", { i, n }), continued: t("rpPrintContinued"), printedBy: t("rpPrintBy", { u: me?.user.name ?? "", d: at }), noRows: t("rpPrintNoRows") }
    };
  };
}

/** Print (browser dialog or PDF, from the report's own layout) and, when this PC has one, print on its A4 printer directly. */
export function ReportPrintButtons({ data, disabled }: { data: () => ReportPrintData; disabled?: boolean }) {
  const { t } = useLang();
  const a4 = useA4Printer();
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function direct() {
    setBusy(true); setStatus(t("rpPrinting"));
    const d = data();
    const logo = await loadPrintImage("/api/organization/logo");
    const v = await printUnrecorded("a4", `report:${Date.now()}`, d.title, () => reportDoc(d, logo), d.printedBy, () => undefined);
    const why = (r: string) => (t(`printReason_${r}`) !== `printReason_${r}` ? t(`printReason_${r}`) : r);
    setStatus(v.kind === "done" ? t("rpPrintSent", { printer: v.printer ?? a4?.printer ?? "" }) : v.kind === "failed" ? t("rpPrintFailed", { x: why(v.reason) }) : v.kind === "unknown" ? t("rpPrintUnknown", { x: why(v.reason) }) : null);
    setBusy(false);
  }
  return (
    <>
      <button className="btn btn-sm" onClick={() => void printReport(data())} disabled={disabled} data-testid="rp-print"><Icon name="printer" />{t(a4 ? "rpPrintBrowser" : "rpPrint")}</button>
      {a4 && <button className="btn btn-sm" onClick={() => void direct()} disabled={disabled || busy} data-testid="rp-print-direct"><Icon name="printer" />{t("rpPrintOn", { printer: a4.printer })}</button>}
      {status && <span className="hint" role="status" data-testid="rp-print-status">{status}</span>}
    </>
  );
}

/** Results card with Excel export (an .xls table, as the legacy build makes it in the browser), Print and the totals row. */
export function ReportTable<R>({ title, file, cols, rows, foot, rowCls, chips, testId, cls, notes }: {
  title: string; file: string; cols: RCol<R>[]; rows: R[]; foot?: [ReactNode, string][]; rowCls?(r: R): string; chips?: ReactNode; testId?: string; cls?: string;
  /** Printed after the table: reconciliation and warnings. */
  notes?: string[];
}) {
  const { t, lang } = useLang();
  const printData = usePrintData();
  const paper = cols.filter((c) => !c.screenOnly);
  const paperFoot = foot?.filter((_, i) => !cols[i]?.screenOnly);
  function exportXls() {
    const cols = paper, foot = paperFoot;
    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const head = `<tr>${cols.map((c) => `<th>${esc((c.grp ? c.grp + " – " : "") + c.label)}</th>`).join("")}</tr>`;
    const body = rows.map((r) => `<tr>${cols.map((c) => `<td>${esc(c.text(r))}</td>`).join("")}</tr>`).join("");
    const tail = foot ? `<tr>${foot.map(([, x]) => `<td>${esc(x)}</td>`).join("")}</tr>` : "";
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body dir="${lang === "ar" ? "rtl" : "ltr"}"><table border="1"><tr><th colspan="${cols.length}">${esc(title)}</th></tr>${head}${body}${tail}</table></body></html>`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + html], { type: "application/vnd.ms-excel" }));
    a.download = `${file}-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a); a.click(); a.remove();
  }
  const data = () => printData(title, paper.map((c) => ({ label: c.label, grp: c.grp, end: c.end })),
    rows.map((r) => ({ cells: paper.map((c) => c.text(r)), kind: kindOf(rowCls?.(r)) })), paperFoot?.map(([, x]) => x), notes);
  return (
    <section className="card rp-res" data-testid={testId}>
      <div className="card-head"><h2>{t("rpResults")} <span className="count num">{rows.length}</span></h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn-sm" onClick={exportXls} disabled={!rows.length}><Icon name="file" />{t("excelExport")}</button>
          <ReportPrintButtons data={data} />
        </div></div>
      {chips && <div className="rp-chips">{chips}</div>}
      <div className="table-wrap"><table className={`tbl rp-tbl ${cls ?? ""}`}>
        <thead><Head cols={cols} /></thead>
        <tbody>{rows.length ? rows.map((r, i) => <tr key={i} className={rowCls?.(r) ?? ""}>{cols.map((c, j) => <td key={j} className={c.end ? "end num" : ""}>{c.cell(r)}</td>)}</tr>)
          : <tr><td colSpan={cols.length}><div className="empty"><Icon name="file" /><div>{t("noResults")}</div></div></td></tr>}</tbody>
        {rows.length > 0 && foot && <tfoot><tr>{foot.map(([x], i) => <td key={i} className={i ? "end num" : ""}><b>{x}</b></td>)}</tr></tfoot>}
      </table></div>
    </section>
  );
}

/** The period of a loaded report as a chip ("Period: 2026-01-01 – 2026-12-31"). */
export function PeriodChip({ from, to }: { from: string | null; to: string | null }) {
  const { t } = useLang();
  return <span className="fchip">{t("periodF")}: <b className="num">{from || to ? `${from || "…"} – ${to || "…"}` : t("pr_all")}</b></span>;
}
